import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import './AppPage.css';
import './ReportsPage.css';
import { translate } from '../utils/translations';

const options: Record<string, string[]> = {
  status: ['ACTIVE', 'LEAVE', 'VACATION', 'SUSPENDED', 'INACTIVE'],
  leaveStatus: ['DRAFT', 'PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'],
  documentStatus: ['ACTIVE', 'INACTIVE'],
  signatureType: ['CONFORMITY', 'NON_CONFORMITY'],
};
export default function ReportsPage() {
  const nav = useNavigate(),
    [catalogs, setCatalogs] = useState<any>({
      employees: [],
      positions: [],
      sectors: [],
      branches: [],
      leaveTypes: [],
      documentTypes: [],
    }),
    [filters, setFilters] = useState<any>({}),
    [rows, setRows] = useState<any[]>([]),
    [loading, setLoading] = useState(false),
    [showFilters, setShowFilters] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      setRows((await apiClient.get('/reports/integral', { params: filters })).data.rows);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    apiClient.get('/reports/catalogs').then((r) => setCatalogs(r.data));
    void load();
  }, []);
  const select = (key: string, label: string, data: any[]) => (
    <label>
      {label}
      <select
        value={filters[key] || ''}
        onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
      >
        <option value="">Todos</option>
        {data.map((x) => (
          <option key={x.id || x} value={x.id || x}>
            {x.name || translate(x)}
          </option>
        ))}
      </select>
    </label>
  );
  const download = async (format: string) => {
    const r = await apiClient.get(`/reports/integral/export/${format}`, {
      params: filters,
      responseType: 'blob',
    });
    const url = URL.createObjectURL(r.data),
      a = document.createElement('a');
    a.href = url;
    a.download = `reporte-rrhh.${format === 'excel' ? 'xls' : 'pdf'}`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="app-page">
      <header className="app-bar">
        <button className="back-button" onClick={() => nav('/dashboard')}>
          ← Volver
        </button>
        <h1>Reportes de Recursos Humanos</h1>
      </header>
      <main className="app-main">
        <div className="page-heading">
          <div>
            <span className="eyebrow">Información integral</span>
            <h2>Reportes</h2>
            <p>Consultá y exportá actividad laboral, documental y de ausencias.</p>
          </div>
          <div className="summary-pill">
            <strong>{rows.length}</strong>
            <span>resultados</span>
          </div>
        </div>
        <div className="report-toolbar">
          <div className="report-toolbar-info">
            <button className="secondary-button" onClick={() => setShowFilters(!showFilters)}>
              {showFilters ? 'Ocultar filtros' : 'Filtrar resultados'}
            </button>
            {Object.values(filters).filter(Boolean).length > 0 && (
              <span className="filter-count">
                {Object.values(filters).filter(Boolean).length} filtros activos
              </span>
            )}
          </div>
          <div className="row-actions">
            <button className="secondary-button" onClick={() => download('excel')}>
              Exportar Excel
            </button>
            <button className="secondary-button" onClick={() => download('pdf')}>
              Exportar PDF
            </button>
          </div>
        </div>
        {showFilters && (
          <section className="panel report-filter-panel">
            <div className="report-filters">
              {select('employeeId', 'Empleado', catalogs.employees)}
              {select('positionId', 'Puesto', catalogs.positions)}
              {select('sectorId', 'Sector', catalogs.sectors)}
              {select('branchId', 'Sucursal', catalogs.branches)}
              {select('status', 'Estado laboral', options.status)}
              {select('leaveTypeId', 'Tipo de licencia', catalogs.leaveTypes)}
              {select('leaveStatus', 'Estado de licencia', options.leaveStatus)}
              {select('documentTypeId', 'Tipo de documento', catalogs.documentTypes)}
              {select('documentStatus', 'Estado documental', options.documentStatus)}
              {select('signatureType', 'Tipo de firma', options.signatureType)}
              <label>
                Tipo de evento
                <select
                  value={filters.reportType || ''}
                  onChange={(e) => setFilters({ ...filters, reportType: e.target.value })}
                >
                  <option value="">Todos</option>
                  <option value="RECEIPT">Recibos</option>
                  <option value="LEAVE">Licencias</option>
                  <option value="DOCUMENT">Documentos</option>
                  <option value="CERTIFICATE">Certificados</option>
                </select>
              </label>
              <label>
                Desde
                <input
                  type="date"
                  value={filters.from || ''}
                  onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                />
              </label>
              <label>
                Hasta
                <input
                  type="date"
                  value={filters.to || ''}
                  onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                />
              </label>
              <div className="report-filter-actions">
                <button className="secondary-button" onClick={() => setFilters({})}>
                  Limpiar
                </button>
                <button
                  className="primary-button"
                  onClick={() => {
                    void load();
                    setShowFilters(false);
                  }}
                >
                  Aplicar filtros
                </button>
              </div>
            </div>
          </section>
        )}
        <section className="panel report-table-panel">
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Empleado</th>
                  <th>Legajo</th>
                  <th>Evento</th>
                  <th>Tipo</th>
                  <th>Estado</th>
                  <th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{new Date(r.date).toLocaleDateString('es-AR')}</td>
                    <td>{r.employee}</td>
                    <td>{r.legajo}</td>
                    <td>{r.event}</td>
                    <td>{r.type}</td>
                    <td>
                      <span
                        className={`status-badge ${['APPROVED', 'ACCEPTED', 'SIGNED', 'ACTIVE'].includes(r.status) ? 'good' : ['REJECTED', 'CANCELLED', 'NON_CONFORMITY', 'EXPIRED'].includes(r.status) ? 'bad' : 'warning'}`}
                      >
                        {translate(r.status)}
                      </span>
                    </td>
                    <td>{r.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!rows.length && (
              <div className="empty-state">
                {loading ? 'Cargando…' : 'No hay resultados para estos filtros.'}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
